"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  Gauge,
  LightbulbIcon,
  Target,
} from "lucide-react";
import { useState } from "react";

interface OfferStrategyProps {
  recommendedOffer: number;
  successProbability: number;
  rationale: string;
  negotiationTips: string[];
}

function SuccessProbabilityIndicator({ probability }: { probability: number }) {
  // Map 0-100 to descriptive text
  const getStatusText = (prob: number) => {
    if (prob >= 80) return "Very High";
    if (prob >= 60) return "High";
    if (prob >= 40) return "Moderate";
    if (prob >= 20) return "Low";
    return "Very Low";
  };

  // Get color based on probability
  const getStatusColor = (prob: number) => {
    if (prob >= 80) return "text-emerald-600";
    if (prob >= 60) return "text-emerald-500";
    if (prob >= 40) return "text-amber-500";
    if (prob >= 20) return "text-orange-500";
    return "text-rose-500";
  };

  return (
    <div className="flex items-center gap-2">
      <Gauge className="h-4 w-4 text-muted-foreground" />
      <div className="flex items-baseline gap-1.5">
        <span className="text-sm text-muted-foreground">
          Success Probability:
        </span>
        <span className={cn("font-medium", getStatusColor(probability))}>
          {getStatusText(probability)}
        </span>
        <span className="text-xs text-muted-foreground">({probability}%)</span>
      </div>
    </div>
  );
}

export function OfferStrategy({
  recommendedOffer,
  successProbability,
  rationale,
  negotiationTips,
}: OfferStrategyProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-lg border bg-card p-4 sm:p-6">
      <div className="space-y-6">
        {/* Recommended Offer */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium text-muted-foreground">
              Recommended Offer
            </h4>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold sm:text-3xl">
              ${recommendedOffer.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Success Probability */}
        <SuccessProbabilityIndicator probability={successProbability} />

        {/* Rationale */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <LightbulbIcon className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium text-muted-foreground">
              AI Rationale
            </h4>
          </div>
          <p className="text-sm">{rationale}</p>
        </div>

        {/* Negotiation Tips Section */}
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex w-full items-center justify-between border-t pt-4 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Negotiation Tips
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          </button>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <ul className="space-y-3">
                  {negotiationTips.map((tip, index) => (
                    <motion.li
                      key={tip}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-2 text-sm"
                    >
                      <ArrowRight className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
                      <span>{tip}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
